import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FiArrowRight, FiChevronLeft, FiChevronRight } from "react-icons/fi";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Navigation, Pagination } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import { API_BASE } from "../../config/api";
import { bindStorefrontRefresh } from "../../utils/storefrontRefresh";
import { DEFAULT_JEWELRY_METALS, formatCategoryDisplayName } from "../../utils/materialCatalog";
import p1 from "../Assets/poster.jpeg";
import p2 from "../Assets/poster2.jpeg";
import p3 from "../Assets/poster3.jpeg";
import p4 from "../Assets/poster4.webp";
import p5 from "../Assets/poster5.jpeg";
import p6 from "../Assets/demo_part.jpg";
import "./Slider.css";

const FALLBACK_CATEGORIES = DEFAULT_JEWELRY_METALS.map((name) => ({
  name,
  imageUrl: "",
}));

const DEFAULT_IMAGES = [p1, p2, p3, p4, p5, p6];

const getImage = (item, index) => {
  if (item?.imageUrl?.trim()) {
    return item.imageUrl;
  }

  return DEFAULT_IMAGES[index % DEFAULT_IMAGES.length];
};

const CategorySlider = () => {
  const [categories, setCategories] = useState(FALLBACK_CATEGORIES);
  const navigate = useNavigate();

  const fetchCategories = useCallback(() => {
    fetch(`${API_BASE}/category-slider`)
      .then((response) => response.json())
      .then((data) => {
        const items = data?.items;

        if (Array.isArray(items) && items.length > 0) {
          setCategories(items);
          return;
        }

        setCategories(FALLBACK_CATEGORIES);
      })
      .catch(() => setCategories(FALLBACK_CATEGORIES));
  }, []);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  useEffect(() => bindStorefrontRefresh(fetchCategories, { scopes: ["homepage"] }), [fetchCategories]);

  const handleCategoryClick = (categoryName) => {
    navigate(`/store?material=${encodeURIComponent(categoryName)}`);
  };

  const showSliderControls = categories.length > 1;

  return (
    <section className="lux-category-section" aria-labelledby="shop-by-category-heading">
      <div className="lux-category-shell">
        <header className="lux-category-header">
          <h2 id="shop-by-category-heading" className="lux-category-header__title">
            SHOP BY CATEGORY
          </h2>
          <div className="lux-category-header__divider" aria-hidden="true">
            <span />
            <i />
            <span />
          </div>
        </header>

        <div className="lux-category-slider-wrap">
          {showSliderControls ? (
            <>
              <button
                type="button"
                className="lux-category-nav lux-category-nav--prev"
                aria-label="View previous categories"
              >
                <FiChevronLeft />
              </button>
              <button
                type="button"
                className="lux-category-nav lux-category-nav--next"
                aria-label="View next categories"
              >
                <FiChevronRight />
              </button>
            </>
          ) : null}

          <Swiper
            className="lux-category-slider"
            modules={[Autoplay, Navigation, Pagination]}
            slidesPerView={1}
            spaceBetween={14}
            loop={categories.length > 4}
            navigation={
              showSliderControls
                ? {
                    prevEl: ".lux-category-nav--prev",
                    nextEl: ".lux-category-nav--next",
                  }
                : false
            }
            pagination={
              showSliderControls
                ? {
                    el: ".lux-category-pagination",
                    clickable: true,
                  }
                : false
            }
            autoplay={
              showSliderControls
                ? {
                    delay: 4200,
                    disableOnInteraction: false,
                    pauseOnMouseEnter: true,
                  }
                : false
            }
            breakpoints={{
              0: { slidesPerView: 2, spaceBetween: 10 },
              768: { slidesPerView: 2, spaceBetween: 16 },
              1024: { slidesPerView: 4, spaceBetween: 18 },
            }}
          >
            {categories.map((category, index) => {
              const displayName = formatCategoryDisplayName(category.name);

              return (
                <SwiperSlide key={`${category.name}-${index}`}>
                  <button
                    type="button"
                    className="lux-category-card"
                    onClick={() => handleCategoryClick(category.name)}
                    aria-label={`Explore ${displayName}`}
                  >
                    <div className="lux-category-card__media">
                      <img
                        src={getImage(category, index)}
                        alt={`${displayName} category`}
                        className="lux-category-card__image"
                        loading="lazy"
                      />
                    </div>

                    <div className="lux-category-card__body">
                      <h3>{displayName}</h3>
                      <span className="lux-category-card__cta">
                        Explore <FiArrowRight aria-hidden="true" />
                      </span>
                    </div>
                  </button>
                </SwiperSlide>
              );
            })}
          </Swiper>

          {showSliderControls ? <div className="lux-category-pagination" aria-hidden="true" /> : null}
        </div>
      </div>
    </section>
  );
};

export default CategorySlider;
